import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, Search, Loader2 } from "lucide-react";
import { apiFetch } from "../../../lib/apiFetch";

interface AddTickerModalProps {
  isOpen: boolean;
  portfolioType?: "REGULAR" | "WATCHLIST" | string;
  onConfirm: (symbol: string, allocation: string) => void;
  onCancel: () => void;
}

export function AddTickerModal({
  isOpen,
  portfolioType = "REGULAR",
  onConfirm,
  onCancel,
}: AddTickerModalProps) {
  const { t } = useTranslation();
  const [symbol, setSymbol] = useState("");
  const [allocation, setAllocation] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const symbolInputRef = useRef<HTMLInputElement>(null);
  const allocationInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isWatchlist = portfolioType === "WATCHLIST";

  useEffect(() => {
    if (isOpen) {
      setSymbol("");
      setAllocation("");
      setResults([]);
      setIsSearching(false);
      setShowSuggestions(false);
      setTimeout(() => {
        symbolInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      const query = symbol.trim();
      if (query.length >= 2) {
        searchTickers(query);
      } else {
        setResults([]);
        setShowSuggestions(false);
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [symbol]);

  const searchTickers = async (q: string) => {
    setIsSearching(true);
    try {
      const res = await apiFetch(`/api/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults(data.results || []);
      setShowSuggestions((data.results || []).length > 0);
    } catch {
      setResults([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirm = () => {
    const trimmedSymbol = symbol.trim().toUpperCase();
    if (!trimmedSymbol) return;

    const trimmedAlloc = isWatchlist ? "0" : allocation.trim() || "0";
    onConfirm(trimmedSymbol, trimmedAlloc);
  };

  const handleSelectSuggestion = (suggestedSymbol: string) => {
    setSymbol(suggestedSymbol.toUpperCase());
    setShowSuggestions(false);
    if (!isWatchlist) {
      allocationInputRef.current?.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-container"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      {/* Modal Dialog Content - stops propagation of clicks/events inside */}
      <div
        className="modal-content max-w-md"
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-800 pb-3 mb-1">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
            <Plus size={16} className="text-emerald-400" />
            {t("portfolio.add_ticker")}
          </h3>
          <button
            onClick={onCancel}
            className="text-zinc-400 hover:text-zinc-200 transition-colors p-1 rounded-sm"
            aria-label={t("common.cancel")}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-4 py-1">
          {/* Ticker Symbol Input */}
          <div className="relative">
            <label htmlFor="ticker-symbol-input" className="block text-[10px] uppercase font-bold tracking-widest text-zinc-400 mb-1">
              {t("portfolio.ticker_symbol", { defaultValue: "Ticker Symbol" })}
            </label>
            <div className="relative flex items-center">
              <input
                id="ticker-symbol-input"
                ref={symbolInputRef}
                name="ticker-symbol"
                aria-label={t("portfolio.ticker_symbol", { defaultValue: "Ticker Symbol" })}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (showSuggestions && results.length > 0) {
                      handleSelectSuggestion(results[0].symbol);
                    } else if (!isWatchlist) {
                      allocationInputRef.current?.focus();
                    } else if (symbol.trim()) {
                      handleConfirm();
                    }
                  } else if (e.key === "Escape") {
                    onCancel();
                  }
                }}
                placeholder="e.g. AAPL, MSFT, SIE.DE"
                className="w-full bg-[#0a0a0a] border border-zinc-800 text-sm px-3 py-2 rounded-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50 font-mono font-bold uppercase tracking-wider"
              />
              <div className="absolute right-3 flex items-center pointer-events-none">
                {isSearching ? (
                  <Loader2 size={14} className="animate-spin text-emerald-500" />
                ) : (
                  <Search size={14} className="text-zinc-500" />
                )}
              </div>
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && results.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#18181b] border border-zinc-800 rounded-sm shadow-2xl z-50 max-h-48 overflow-y-auto">
                {results.map((item, idx) => (
                  <div
                    key={idx}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectSuggestion(item.symbol);
                    }}
                    onClick={() => handleSelectSuggestion(item.symbol)}
                    className="px-3 py-2 border-b border-zinc-800/50 last:border-b-0 hover:bg-zinc-800/80 cursor-pointer flex justify-between items-center transition-colors"
                  >
                    <div>
                      <span className="font-mono text-xs font-bold text-emerald-400 mr-2">
                        {item.symbol}
                      </span>
                      <span className="text-[10px] text-zinc-400 truncate max-w-[180px] inline-block align-bottom">
                        {item.longname || item.shortname || item.name}
                      </span>
                    </div>
                    <span className="text-[9px] uppercase font-mono tracking-widest text-zinc-500 px-1.5 py-0.5 bg-zinc-900 rounded border border-zinc-800">
                      {item.exchDisp || item.exchange}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Allocation Input (Only for non-watchlist portfolios) */}
          {!isWatchlist && (
            <div>
              <label htmlFor="ticker-allocation-input" className="block text-[10px] uppercase font-bold tracking-widest text-zinc-400 mb-1">
                {t("portfolio.allocation_pct", { defaultValue: "Allocation (%)" })}
              </label>
              <input
                id="ticker-allocation-input"
                ref={allocationInputRef}
                name="ticker-allocation"
                aria-label={t("portfolio.allocation_pct", { defaultValue: "Allocation (%)" })}
                type="number"
                step="any"
                min="0"
                max="100"
                value={allocation}
                onChange={(e) => setAllocation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (symbol.trim()) handleConfirm();
                  } else if (e.key === "Escape") {
                    onCancel();
                  }
                }}
                placeholder="e.g. 10"
                className="w-full bg-[#0a0a0a] border border-zinc-800 text-sm px-3 py-2 rounded-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50 font-mono"
              />
            </div>
          )}
        </div>

        {/* Modal Action Buttons */}
        <div className="flex justify-end gap-3 mt-3 pt-3 border-t border-zinc-800">
          <button
            onClick={onCancel}
            className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1.5 rounded-sm transition-colors uppercase font-bold tracking-wider"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!symbol.trim()}
            className="text-xs bg-emerald-900/40 text-emerald-400 border border-emerald-500/30 px-4 py-1.5 rounded-sm hover:bg-emerald-800/50 disabled:opacity-40 disabled:hover:bg-emerald-900/40 transition-colors uppercase font-bold tracking-wider flex items-center gap-1.5"
          >
            <Plus size={14} />
            {t("common.add", { defaultValue: "Add" })}
          </button>
        </div>
      </div>
    </div>
  );
}
